import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { CheckCircle2, Heart, Loader2, Lock, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fetchPaymentConfig, getStripe, stripeAppearance } from "@/lib/stripeTheme";

const EASE = [0.16, 1, 0.3, 1];
const PRESETS = [10, 25, 50, 100, 250];

function DonationPaymentForm({ donation, donor, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setPaying(true);
    setError("");
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/#donate`,
        payment_method_data: { billing_details: { name: donor.name, email: donor.email } },
      },
    });
    if (err) {
      setError(err.message || "Payment failed. Please try another card.");
      setPaying(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      await onPaid(paymentIntent.id);
    } else {
      setError("Your donation is processing — thank you!");
      setPaying(false);
    }
  };

  return (
    <form onSubmit={pay} data-testid="donation-payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p data-testid="donation-payment-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>}
      <Button data-testid="donation-pay-button" type="submit" disabled={!stripe || paying}
        className="mt-6 w-full rounded-full bg-gold py-6 text-base font-semibold text-ink transition-all hover:bg-gold-hover hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]">
        {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <><Lock className="mr-2 h-4 w-4" /> Donate ${donation.amount.toFixed(2)}{donation.interval === "monthly" ? " / month" : ""}</>
        )}
      </Button>
    </form>
  );
}

export default function DonateSection() {
  const [c, setC] = useState({
    donate_heading: "Fuel the mission with a donation.",
    donate_subcopy: "Your gift funds mentorship academies, free community workshops, and leadership programs for youth ages 10–16. Give once — or become a monthly bridge-builder.",
  });
  const [preset, setPreset] = useState(25);
  const [custom, setCustom] = useState("");
  const [interval, setInterval_] = useState("once");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null); // {client_secret, donation_id, amount, interval}
  const [stripeKey, setStripeKey] = useState(null);
  const [done, setDone] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [wallOptIn, setWallOptIn] = useState(false);
  const [wall, setWall] = useState({ enabled: false, entries: [] });

  const loadWall = () => api.get("/donations/wall").then(({ data }) => setWall(data)).catch(() => {});

  useEffect(() => {
    api.get("/content")
      .then(({ data }) => setC((prev) => ({ ...prev, ...data })))
      .catch(() => {});
    api.get("/campaigns/active")
      .then(({ data }) => {
        setCampaigns(data);
        if (data.length) setCampaignId(data[0].id);
      })
      .catch(() => {});
    loadWall();
  }, []);

  const amount = custom ? parseFloat(custom) || 0 : preset;
  const selectedCampaign = campaigns.find((cp) => cp.id === campaignId) || null;

  const start = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) {
      toast.error("Please choose or enter an amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const [cfg, { data }] = await Promise.all([
        fetchPaymentConfig(),
        api.post("/donations/intent", {
          amount, interval, name: donor.name, email: donor.email,
          show_on_wall: wallOptIn, campaign_id: campaignId || null,
        }),
      ]);
      setStripeKey(cfg.publishable_key);
      setIntent({ ...data, amount, interval });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onPaid = async (piId) => {
    try {
      const { data } = await api.post("/donations/confirm", { payment_intent_id: piId });
      setDone(data.donation);
      toast.success("Thank you for your donation!");
      loadWall();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section id="donate" data-testid="donate-section" className="relative border-y border-gold/10 bg-ink-card/40 py-28 sm:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Give</span>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {c.donate_heading}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
            {c.donate_subcopy}
          </p>
          <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Heart className="h-4 w-4 text-gold" /> 100% funds programs</span>
            <span className="flex items-center gap-2"><Repeat className="h-4 w-4 text-gold" /> Cancel monthly anytime</span>
          </div>
          {wall.enabled && wall.entries.length > 0 && (
            <div className="mt-10" data-testid="bridge-builders-wall">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Bridge-Builders</p>
              <ul className="mt-4 space-y-2.5">
                {wall.entries.map((e, i) => (
                  <li key={i} data-testid={`wall-entry-${i}`} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Heart className="h-3.5 w-3.5 shrink-0 text-gold" />
                    <span>
                      <span className="font-semibold text-foreground">{e.name}</span> gave ${Number(e.amount).toFixed(0)}{e.interval === "monthly" ? "/mo" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-3xl border border-gold/25 bg-ink-card/80 p-7 backdrop-blur sm:p-10"
        >
          {done ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="donation-success">
              <CheckCircle2 className="h-16 w-16 text-gold" />
              <h3 className="mt-5 font-display text-2xl font-semibold">Thank you, {done.donor?.name?.split(" ")[0] || "friend"}!</h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                Your {done.interval === "monthly" ? "monthly" : "one-time"} gift of ${Number(done.amount).toFixed(2)}
                {done.interval === "monthly" ? " will repeat each month" : ""} is confirmed. A receipt is on its way to {done.donor?.email}.
              </p>
              <Button data-testid="donation-again-button" variant="outline" onClick={() => { setDone(null); setIntent(null); setCustom(""); setPreset(25); }}
                className="mt-7 rounded-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                Make Another Gift
              </Button>
            </div>
          ) : !intent ? (
            <form onSubmit={start} data-testid="donation-form" className="space-y-5">
              {campaigns.length > 0 && (
                <div data-testid="campaign-block" className="rounded-xl border border-gold/20 bg-ink/70 p-4">
                  {campaigns.length > 1 && (
                    <select
                      data-testid="campaign-select"
                      value={campaignId}
                      onChange={(e) => setCampaignId(e.target.value)}
                      className="mb-3 w-full rounded-lg border border-gold/20 bg-ink px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none"
                    >
                      <option value="">Where it's most needed</option>
                      {campaigns.map((cp) => (
                        <option key={cp.id} value={cp.id}>{cp.title}</option>
                      ))}
                    </select>
                  )}
                  {selectedCampaign ? (
                    <>
                      <div className="flex items-baseline justify-between gap-3">
                        <p data-testid="campaign-title" className="font-display text-sm font-semibold text-foreground">{selectedCampaign.title}</p>
                        <p className="font-mono text-xs text-gold">{selectedCampaign.percent}%</p>
                      </div>
                      <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-gold/10" data-testid="campaign-progress">
                        <motion.div
                          className="h-full rounded-full bg-gold"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${Math.max(2, selectedCampaign.percent)}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, ease: EASE }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        ${selectedCampaign.raised.toLocaleString()} raised of ${selectedCampaign.goal.toLocaleString()} goal
                        {selectedCampaign.end_date ? ` · ends ${new Date(selectedCampaign.end_date + "T12:00:00").toLocaleDateString()}` : ""}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">Your gift will go where it's most needed.</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Frequency">
                {[["once", "One-time"], ["monthly", "Monthly"]].map(([v, l]) => (
                  <button type="button" key={v} data-testid={`donate-interval-${v}`} onClick={() => setInterval_(v)}
                    aria-pressed={interval === v}
                    className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                      interval === v ? "border-gold bg-gold/10 text-gold" : "border-gold/15 text-muted-foreground hover:border-gold/40"
                    }`}>
                    {l}{v === "monthly" && <Repeat className="ml-1.5 inline h-3.5 w-3.5" />}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-2" role="radiogroup" aria-label="Amount">
                {PRESETS.map((p) => (
                  <button type="button" key={p} data-testid={`donate-preset-${p}`}
                    onClick={() => { setPreset(p); setCustom(""); }}
                    aria-pressed={!custom && preset === p}
                    className={`rounded-xl border px-2 py-3 font-display text-sm font-semibold transition-all ${
                      !custom && preset === p ? "border-gold bg-gold text-ink" : "border-gold/15 text-muted-foreground hover:border-gold/40 hover:text-foreground"
                    }`}>
                    ${p}
                  </button>
                ))}
              </div>
              <div>
                <Label htmlFor="donate-custom">Or enter your own amount</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-lg text-gold">$</span>
                  <Input id="donate-custom" data-testid="donate-custom-amount" type="number" min="1" step="1" placeholder="Custom amount"
                    value={custom} onChange={(e) => setCustom(e.target.value)}
                    className="border-gold/20 bg-ink pl-9 font-display text-lg" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="donate-name">Name</Label>
                  <Input id="donate-name" data-testid="donate-name-input" required value={donor.name}
                    onChange={(e) => setDonor({ ...donor, name: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                </div>
                <div>
                  <Label htmlFor="donate-email">Email</Label>
                  <Input id="donate-email" data-testid="donate-email-input" type="email" required value={donor.email}
                    onChange={(e) => setDonor({ ...donor, email: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                </div>
              </div>
              {wall.enabled && (
                <label className="flex cursor-pointer items-start gap-3 text-sm" data-testid="wall-optin">
                  <input
                    type="checkbox"
                    data-testid="wall-optin-checkbox"
                    checked={wallOptIn}
                    onChange={(e) => setWallOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37]"
                  />
                  <span className="text-muted-foreground">
                      Show my first name on the public <span className="font-semibold text-gold">Bridge-Builders</span> wall
                  </span>
                </label>
              )}
              <Button data-testid="donate-continue-button" type="submit" disabled={loading}
                className="w-full rounded-full bg-gold py-6 text-base font-semibold text-ink transition-all hover:bg-gold-hover">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <><Heart className="mr-2 h-4 w-4" /> Donate ${amount.toFixed(2)}{interval === "monthly" ? " / month" : ""}</>
                )}
              </Button>
              <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5 text-gold" /> Secure donation processed by Stripe
              </p>
            </form>
          ) : (
            <div data-testid="donation-payment-step">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-semibold">Card details</h3>
                <button data-testid="donation-edit-button" onClick={() => setIntent(null)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-gold hover:underline">
                  Edit amount
                </button>
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                ${intent.amount.toFixed(2)}{intent.interval === "monthly" ? " monthly" : ""} · {donor.name} · {donor.email}
              </p>
              <div className="mt-6">
                {stripeKey ? (
                  <Elements stripe={getStripe(stripeKey)} options={{ clientSecret: intent.client_secret, appearance: stripeAppearance }}>
                    <DonationPaymentForm donation={intent} donor={donor} onPaid={onPaid} />
                  </Elements>
                ) : (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
